/**
 * Multi-Step Offer Modal Controller with Enhanced Error Handling Plus
 * Handles network failures, API errors, and provides user-friendly feedback
 */

const MultiStepOfferModal = {
  selectedCurrency: null,
  currentStep: 1,
  retryCount: 0,
  maxRetries: 3,
  
  /**
   * Initialize the modal
   */
  init() {
    this.bindEvents();
    this.setupErrorRecovery();
    this.setupInputSanitization();
  },

  /**
   * Bind event listeners
   */
  bindEvents() {
    const openModalBtn = document.getElementById('open-offer-modal');
    if (openModalBtn) {
      openModalBtn.addEventListener('click', () => {
        this.openModal();
      });
    }
  },

  /**
   * Setup error recovery mechanisms
   */
  setupErrorRecovery() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.showNetworkStatus('Connected! You can continue making your offer.', 'success');
      // Try to update currency rates if we're in the currency step
      if (typeof CurrencyConverter !== 'undefined') {
        CurrencyConverter.forceUpdate();
      }
    });
    
    window.addEventListener('offline', () => {
      this.showNetworkStatus('You appear to be offline. Your offer will be submitted when connection is restored.', 'warning');
    });
  },

  /**
   * Setup real-time input sanitization on form fields
   */
  setupInputSanitization() {
    // Sanitize email input in real-time
    const emailInput = document.getElementById('offerEmail');
    if (emailInput) {
      emailInput.addEventListener('input', (e) => {
        const sanitized = e.target.value.replace(/[^\w@.-]/g, '').substring(0, 100);
        if (sanitized !== e.target.value) {
          e.target.value = sanitized;
        }
      });
    }
    
    // Limit numeric input to reasonable values
    const offerInput = document.getElementById('offerAmount');
    if (offerInput) {
      offerInput.addEventListener('input', (e) => {
        let value = e.target.value;
        // Remove any non-numeric characters except decimal point
        value = value.replace(/[^0-9.]/g, '');
        // Limit to reasonable range (max $999,999)
        const numValue = parseFloat(value) || 0;
        if (numValue > 999999) {
          value = '999999';
        }
        if (value !== e.target.value) {
          e.target.value = value;
        }
      });
    }
  },

  /**
   * Sanitize user input to prevent XSS and other injection attacks
   */
  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    // Remove HTML tags and potentially dangerous characters
    return input
      .replace(/[<>]/g, '') // Remove angle brackets
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
      .replace(/[\x00-\x1f\x7f-\x9f]/g, '') // Remove control characters
      .trim()
      .substring(0, 255); // Limit length
  },

  /**
   * Validate and sanitize email address
   */
  sanitizeEmail(email) {
    if (!email) return '';
    
    // Basic email sanitization
    const sanitized = email
      .toLowerCase()
      .trim()
      .replace(/[^\w@.-]/g, '') // Only allow word chars, @, ., -
      .substring(0, 100); // Reasonable email length limit
    
    // Basic email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(sanitized) ? sanitized : '';
  },

  /**
   * Show network status message
   */
  showNetworkStatus(message, type = 'info') {
    const responseDiv = document.getElementById('offer-response');
    if (!responseDiv) return;
    
    const colors = {
      success: { bg: '#d4edda', text: '#155724' },
      warning: { bg: '#fff3cd', text: '#856404' },
      error: { bg: '#f8d7da', text: '#721c24' },
      info: { bg: '#d1ecf1', text: '#0c5460' }
    };
    
    const color = colors[type] || colors.info;
    
    responseDiv.innerHTML = `
      <div style="
        background: ${color.bg}; 
        color: ${color.text}; 
        padding: 12px 16px; 
        border-radius: 6px; 
        margin: 16px 0; 
        border: 1px solid ${color.text}20;
        font-size: 14px;
      ">
        ${message}
      </div>
    `;
    
    // Auto-hide success messages
    if (type === 'success') {
      setTimeout(() => {
        if (responseDiv.innerHTML.includes(message)) {
          responseDiv.innerHTML = '';
        }
      }, 5000);
    }
  },

  /**
   * Open the modal and reset to step 1
   */
  openModal() {
    const modal = document.getElementById('offer-modal');
    if (modal) {
      modal.style.display = 'block';
      this.showStep(1);
      this.resetForm();
      this.retryCount = 0;
    }
  },

  /**
   * Close the modal and reset everything
   */
  closeModal() {
    const modal = document.getElementById('offer-modal');
    if (modal) modal.style.display = 'none';
    
    this.resetForm();
    this.selectedCurrency = null;
    this.currentStep = 1;
    this.retryCount = 0;
  },

  /**
   * Reset form fields and displays
   */
  resetForm() {
    // Reset form input fields
    const fields = ['offerAmount', 'offerEmail'];
    fields.forEach(fieldId => {
      const field = document.getElementById(fieldId);
      if (field) field.value = '';
    });
    
    // Reset currency dropdown
    const currencyDropdown = document.getElementById('currency-dropdown');
    if (currencyDropdown) currencyDropdown.value = '';
    
    // Clear response and conversion displays
    const responseDiv = document.getElementById('offer-response');
    if (responseDiv) responseDiv.innerHTML = '';
    
    const conversionDisplay = document.getElementById('conversion-display');
    if (conversionDisplay) conversionDisplay.style.display = 'none';
    
    this.hideLoading();
  },

  /**
   * Handle currency selection from dropdown
   */
  handleCurrencySelection() {
    const dropdown = document.getElementById('currency-dropdown');
    if (!dropdown || !dropdown.value) return;
    
    // Check if currency converter is available
    if (typeof CurrencyConverter === 'undefined') {
      this.showError('Currency converter is not available. Please refresh the page and try again.', true);
      return;
    }
    
    this.selectedCurrency = dropdown.value;
    console.log('Selected currency:', this.selectedCurrency);
    
    // Auto-advance to step 2 after brief delay
    setTimeout(() => {
      this.showStep(2);
    }, 500);
  },

  /**
   * Show specific step
   */
  showStep(stepNumber) {
    // Hide all steps
    for (let i = 1; i <= 3; i++) {
      const step = document.getElementById(`step-${i}`);
      if (step) step.style.display = 'none';
    }
    
    // Show target step
    const targetStep = document.getElementById(`step-${stepNumber}`);
    if (targetStep) targetStep.style.display = 'block';
    
    // Handle step-specific logic
    if (stepNumber === 2) {
      this.updatePriceDisplay();
    } else if (stepNumber === 3) {
      this.setupOfferInput();
    }
    
    this.currentStep = stepNumber;
  },

  /**
   * Update price display in step 2
   */
  updatePriceDisplay() {
    if (!this.selectedCurrency || !window.offerData || typeof CurrencyConverter === 'undefined') {
      this.showError('Unable to load pricing information. Please refresh and try again.', true);
      return;
    }

    try {
      // Check if currency rates are available
      const rateStatus = CurrencyConverter.getRateStatus();
      
      if (rateStatus.status === 'fallback') {
        this.showNetworkStatus('Using cached exchange rates. Prices may not be current.', 'warning');
      }

      // USD amount for display only - handle both old and new price formats
      let usdPriceCents = window.offerData.fullPriceCents || 0;
      if (typeof usdPriceCents === 'string') {
        try {
          // Try to parse as JSON first
          const parsed = JSON.parse(usdPriceCents);
          if (parsed.amount) {
            usdPriceCents = Math.round(parseFloat(parsed.amount) * 100);
          }
        } catch (e) {
          // If JSON parsing fails, try as direct number
          usdPriceCents = Math.round(parseFloat(usdPriceCents) * 100);
        }
      }
      
      const usdPrice = usdPriceCents / 100;
      const convertedPrice = CurrencyConverter.fromUSD(usdPrice, this.selectedCurrency);
      const currencyInfo = CurrencyConverter.getCurrencyInfo(this.selectedCurrency);

      // Update USD price
      const usdPriceEl = document.getElementById('usd-price');
      if (usdPriceEl) {
        usdPriceEl.textContent = CurrencyConverter.formatCurrency(usdPrice, 'USD');
      }

      // Update converted price
      const convertedPriceEl = document.getElementById('converted-price');
      if (convertedPriceEl) {
        convertedPriceEl.textContent = CurrencyConverter.formatCurrency(convertedPrice, this.selectedCurrency);
      }

      // Update currency label
      const currencyLabel = document.getElementById('selected-currency-label');
      if (currencyLabel) {
        currencyLabel.textContent = `${currencyInfo.flag} ${this.selectedCurrency}`;
      }
      
    } catch (error) {
      console.error('Error updating price display:', error);
      this.showError('Unable to calculate prices. Please try selecting your currency again.', false);
    }
  },

  /**
   * Setup offer input in step 3
   */
  setupOfferInput() {
    if (!this.selectedCurrency || typeof CurrencyConverter === 'undefined') {
      this.showError('Currency information is not available. Please go back and select your currency again.', false);
      return;
    }
    
    try {
      const currencyInfo = CurrencyConverter.getCurrencyInfo(this.selectedCurrency);
      const offerLabel = document.getElementById('offer-currency-label');
      const offerInput = document.getElementById('offerAmount');
      const submitBtn = document.getElementById('submit-offer-btn');
      const conversionDisplay = document.getElementById('conversion-display');
      
      if (offerLabel) {
        offerLabel.textContent = `Your Offer (${currencyInfo.symbol}${this.selectedCurrency})`;
      }
      
      if (offerInput) {
        offerInput.placeholder = `Enter amount in ${this.selectedCurrency}`;
        offerInput.step = currencyInfo.decimals === 0 ? '1' : '0.01';
      }
      
      // Hide submit button and conversion display initially
      if (submitBtn) submitBtn.style.display = 'none';
      if (conversionDisplay) conversionDisplay.style.display = 'none';
      
    } catch (error) {
      console.error('Error setting up offer input:', error);
      this.showError('Unable to configure offer form. Please refresh and try again.', true);
    }
  },

  /**
   * Update offer display as user types
   */
  updateOfferDisplay() {
    const offerInput = document.getElementById('offerAmount');
    const conversionDisplay = document.getElementById('conversion-display');
    const offerInCurrency = document.getElementById('offer-in-currency');
    const offerInUsd = document.getElementById('offer-in-usd');
    const offerStatus = document.getElementById('offer-status');
    const submitBtn = document.getElementById('submit-offer-btn');
    
    if (!offerInput || !this.selectedCurrency || typeof CurrencyConverter === 'undefined') return;
    
    const offerAmount = parseFloat(offerInput.value) || 0;
    
    try {
      if (offerAmount > 0) {
        // Show conversion display
        if (conversionDisplay) conversionDisplay.style.display = 'block';
        
        // Update offer amounts (display only)
        const offerUSD = CurrencyConverter.toUSD(offerAmount, this.selectedCurrency);
        const offerUsdCents = Math.round(offerUSD * 100);
        
        if (offerInCurrency) {
          offerInCurrency.textContent = CurrencyConverter.formatCurrency(offerAmount, this.selectedCurrency);
        }
        
        if (offerInUsd) {
          // Display from cents for consistency
          offerInUsd.textContent = CurrencyConverter.formatCurrency(offerUsdCents / 100, 'USD');
        }
        
        // Update status and button visibility based on acceptance ranges (all in cents)
        if (offerStatus && submitBtn && window.offerData) {
          // Parse fullPriceCents - handle both old numeric format and new JSON format
          let fullUsdCents = window.offerData.fullPriceCents;
          if (typeof fullUsdCents === 'string') {
            try {
              const parsed = JSON.parse(fullUsdCents);
              if (parsed.amount) {
                fullUsdCents = Math.round(parseFloat(parsed.amount) * 100);
              }
            } catch (e) {
              fullUsdCents = Math.round(parseFloat(fullUsdCents) * 100);
            }
          }
          
          const autoAcceptCents  = window.offerData.autoAcceptCents ?? Math.round((window.offerData.autoAccept || 0) * 100);
          const autoDeclineCents = window.offerData.autoDeclineCents ?? Math.round((window.offerData.autoDecline || 0) * 100);

          let showSubmitButton = false;

          if (offerUsdCents > fullUsdCents) {
            // Offer exceeds product price
            offerStatus.style.background = '#f8d7da';
            offerStatus.style.color = '#721c24';
            const maxOffer = CurrencyConverter.fromUSD(fullUsdCents / 100, this.selectedCurrency);
            offerStatus.textContent = `Offer too high. Maximum: ${CurrencyConverter.formatCurrency(maxOffer, this.selectedCurrency)}`;
            showSubmitButton = false;

          } else if (offerUsdCents <= autoDeclineCents) {
            // Offer too low
            offerStatus.style.background = '#f8d7da';
            offerStatus.style.color = '#721c24';
            const minAcceptable = CurrencyConverter.fromUSD((autoDeclineCents + 1) / 100, this.selectedCurrency);
            offerStatus.textContent = `Too low. Try ${CurrencyConverter.formatCurrency(minAcceptable, this.selectedCurrency)}+ for consideration.`;
            showSubmitButton = false;

          } else if (offerUsdCents >= autoAcceptCents) {
            // Auto-accept range
            offerStatus.style.background = '#d4edda';
            offerStatus.style.color = '#155724';
            offerStatus.textContent = 'Great offer! This will be automatically accepted.';
            showSubmitButton = true;

          } else {
            // Manual review range
            offerStatus.style.background = '#fff3cd';
            offerStatus.style.color = '#856404';
            offerStatus.textContent = 'Your offer will be reviewed manually.';
            showSubmitButton = true;
          }

          // Show/hide submit button based on validation
          submitBtn.style.display = showSubmitButton ? 'flex' : 'none';
        }
      } else {
        // Hide conversion display when no offer
        if (conversionDisplay) conversionDisplay.style.display = 'none';
        if (submitBtn) submitBtn.style.display = 'none';
      }
      
    } catch (error) {
      console.error('Error updating offer display:', error);
      this.showError('Unable to calculate offer. Please check your amount and try again.', false);
    }
  },

  /**
   * Submit the offer with enhanced error handling
   */
  async submitOffer() {
    if (!this.selectedCurrency || typeof CurrencyConverter === 'undefined') {
      this.showError('Currency information is missing. Please refresh and try again.', true);
      return;
    }
    
    const offerInput = document.getElementById('offerAmount');
    const emailInput = document.getElementById('offerEmail');
    const responseDiv = document.getElementById('offer-response');
    
    if (!offerInput || !emailInput || !responseDiv) {
      this.showError('Form elements are missing. Please refresh the page.', true);
      return;
    }
    
    const offerAmount = parseFloat(offerInput.value) || 0;
    const email = this.sanitizeEmail(emailInput.value);
    
    // Enhanced validation with specific error messages
    if (offerAmount <= 0) {
      this.showError('Please enter a valid offer amount greater than zero.', false);
      offerInput.focus();
      return;
    }
    
    if (!email) {
      this.showError('Please enter a valid email address.', false);
      emailInput.focus();
      return;
    }
    
    // Additional email validation
    if (email.length < 5 || !email.includes('@') || !email.includes('.')) {
      this.showError('Please enter a valid email address.', false);
      emailInput.focus();
      return;
    }
    
    // Check for network connectivity
    if (!navigator.onLine) {
      this.showError('You appear to be offline. Please check your internet connection and try again.', false);
      return;
    }
    
    // Show loading state
    this.showLoading();
    
    try {
      // Convert to USD cents for backend
      const sel = this.selectedCurrency;
      const offerUsdCents = Math.round(CurrencyConverter.toUSD(offerAmount, sel) * 100);
      
      // Parse fullPriceCents - handle both old numeric format and new JSON format
      let fullUsdCents = window.offerData.fullPriceCents;
      if (typeof fullUsdCents === 'string') {
        try {
          // Try to parse as JSON first
          const parsed = JSON.parse(fullUsdCents);
          if (parsed.amount) {
            fullUsdCents = Math.round(parseFloat(parsed.amount) * 100);
          }
        } catch (e) {
          // If JSON parsing fails, try as direct number
          fullUsdCents = Math.round(parseFloat(fullUsdCents) * 100);
        }
      }
      
      const amountOffCents = fullUsdCents - offerUsdCents;

      const payload = {
        product_id: window.offerData.productId,
        variant_id: window.offerData.variantId,
        email: email,
        full_price_usd_cents: fullUsdCents,
        offer_usd_cents: offerUsdCents,
        amount_off_usd_cents: amountOffCents,
        original_currency: sel,
        original_amount: offerAmount,
        auto_accept_price_cents: window.offerData.autoAcceptCents || 0,
        auto_decline_price_cents: window.offerData.autoDeclineCents || 0
      };
      
      console.log('Submitting offer:', payload);
      
      const response = await this.makeResilientRequest(window.offerData.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        await this.handleApiError(response);
        return;
      }
      
      const result = await response.json();
      console.log('API Response:', result);
      
      if (result.checkout_url) {
        this.showSuccess(result, offerAmount);
      } else {
        this.showError('Offer was accepted but checkout link is not available. Please contact support.', false);
      }
      
    } catch (error) {
      this.handleRequestError(error);
    } finally {
      this.hideLoading();
    }
  },

  /**
   * Make a resilient HTTP request with retries
   */
  async makeResilientRequest(url, options, retries = this.maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. The server may be busy.');
      }
      
      if (retries > 0 && (error.name === 'TypeError' || error.message.includes('Failed to fetch'))) {
        console.log(`Request failed, retrying... (${this.maxRetries - retries + 1}/${this.maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (this.maxRetries - retries + 1))); // Progressive delay
        return this.makeResilientRequest(url, options, retries - 1);
      }
      
      throw error;
    }
  },

  /**
   * Handle API error responses
   */
  async handleApiError(response) {
    let errorMessage = 'An unexpected error occurred. Please try again.';
    let shouldRetry = false;
    
    try {
      const errorData = await response.json();
      
      if (response.status === 429) {
        // Rate limited
        const retryAfter = errorData.retry_after || 60;
        const minutes = Math.ceil(retryAfter / 60);
        errorMessage = errorData.error || `Too many requests. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
        
      } else if (response.status === 400) {
        // Bad request - user error
        errorMessage = errorData.error || 'Please check your information and try again.';
        
      } else if (response.status >= 500) {
        // Server error - might be temporary
        errorMessage = 'Our servers are temporarily busy. Please try again in a moment.';
        shouldRetry = this.retryCount < this.maxRetries;
        
      } else if (response.status === 422) {
        // Validation error
        errorMessage = errorData.error || 'Unable to process your offer. Please try a different amount.';
        
      } else {
        // Other errors
        errorMessage = errorData.error || `Service error (${response.status}). Please try again.`;
      }
      
    } catch (e) {
      // Couldn't parse error response
      errorMessage = `Service error (${response.status}). Please try again.`;
    }
    
    if (shouldRetry) {
      this.retryCount++;
      errorMessage += ` Retrying automatically... (${this.retryCount}/${this.maxRetries})`;
      this.showError(errorMessage, false);
      
      setTimeout(() => {
        this.submitOffer();
      }, 2000);
    } else {
      this.showError(errorMessage, false);
    }
  },

  /**
   * Handle request errors (network issues, etc.)
   */
  handleRequestError(error) {
    console.error('Request error:', error);
    
    let errorMessage;
    let shouldRetry = false;
    
    if (error.message.includes('timed out') || error.message.includes('timeout')) {
      errorMessage = 'Request timed out. The server may be busy. Please try again.';
      shouldRetry = this.retryCount < this.maxRetries;
      
    } else if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
      shouldRetry = this.retryCount < this.maxRetries;
      
    } else {
      errorMessage = 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
    
    if (shouldRetry) {
      this.retryCount++;
      errorMessage += ` Retrying automatically... (${this.retryCount}/${this.maxRetries})`;
      this.showError(errorMessage, false);
      
      setTimeout(() => {
        this.submitOffer();
      }, 2000);
    } else {
      this.showError(errorMessage, false);
    }
  },

  /**
   * Show error message with optional refresh suggestion
   */
  showError(message, suggestRefresh = false) {
    const responseDiv = document.getElementById('offer-response');
    if (!responseDiv) return;
    
    // Sanitize the message to prevent XSS
    const sanitizedMessage = this.sanitizeInput(message);
    
    let fullMessage = sanitizedMessage;
    if (suggestRefresh) {
      fullMessage += ' You may need to refresh the page.';
    }
    
    responseDiv.innerHTML = `
      <div style="
        background: #f8d7da; 
        color: #721c24; 
        padding: 16px; 
        border-radius: 8px; 
        margin: 16px 0; 
        border: 1px solid #f5c6cb;
        font-size: 16px;
        line-height: 1.4;
      ">
        ${fullMessage}
        ${suggestRefresh ? `
          <div style="margin-top: 12px;">
            <button onclick="window.location.reload()" style="
              background: #dc3545; 
              color: white; 
              border: none; 
              padding: 8px 16px; 
              border-radius: 4px; 
              cursor: pointer; 
              font-size: 14px;
            ">
              Refresh Page
            </button>
          </div>
        ` : ''}
      </div>
    `;
  },

  /**
   * Show success message
   */
  showSuccess(result, offerAmount) {
    // Hide the form content and show clean success screen
    const step3 = document.getElementById('step-3');
    if (step3) step3.style.display = 'none';
    
    const responseDiv = document.getElementById('offer-response');
    if (!responseDiv) return;
    
    // Check if offer is in manual review status
    if (result.status === 'MANUAL REVIEW') {
      responseDiv.innerHTML = `
        <div style="text-align: center; padding: 8px;">
          <div style="font-size: 48px; margin-bottom: 16px;">⏳</div>
          <h3 style="margin: 0 0 16px 0; font-size: 24px; color: #333; font-weight: 600;">
            Offer Submitted!
          </h3>
          <p style="margin: 0 0 24px 0; font-size: 16px; color: #666; line-height: 1.6;">
            Thank you for your ${CurrencyConverter.formatCurrency(offerAmount, this.selectedCurrency)} offer!
          </p>
          <div style="
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 24px;
          ">
            <p style="margin: 0; font-size: 14px; color: #856404; line-height: 1.6;">
              <strong>📧 Under Review</strong><br>
              Your offer is being reviewed by our team. We'll email you at <strong>${this.sanitizeInput(result.email || 'your email')}</strong> within 24 hours with our decision.
            </p>
          </div>
          <button onclick="closeOfferModal()" style="
            background: #007cba;
            color: white;
            padding: 12px 32px;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            font-size: 16px;
            cursor: pointer;
            transition: background 0.2s ease;
          " onmouseover="this.style.background='#005a87'" onmouseout="this.style.background='#007cba'">
            Close
          </button>
        </div>
      `;
    } else {
      // Offer was automatically accepted
      responseDiv.innerHTML = `
        <div style="text-align: center; padding: 8px;">
          <div style="font-size: 48px; margin-bottom: 16px;">🎉</div>
          <h3 style="margin: 0 0 16px 0; font-size: 24px; color: #333; font-weight: 600;">
            Offer Accepted!
          </h3>
          <p style="margin: 0 0 24px 0; font-size: 16px; color: #666;">
            Your ${CurrencyConverter.formatCurrency(offerAmount, this.selectedCurrency)} offer is ready for checkout.
          </p>
          <a href="${result.checkout_url}" class="btn" target="_blank" rel="noopener noreferrer" style="
            display: inline-block;
            background: #007cba;
            color: white;
            padding: 16px 32px;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            font-size: 16px;
            transition: background 0.2s ease;
          " onmouseover="this.style.background='#005a87'" onmouseout="this.style.background='#007cba'">
            Complete Purchase →
          </a>
        </div>
      `;
    }
  },

  /**
   * Show loading state
   */
  showLoading() {
    const submitBtn = document.getElementById('submit-offer-btn');
    const submitText = document.getElementById('submit-text');
    const spinner = document.getElementById('loading-spinner');
    
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.background = '#999';
      submitBtn.style.cursor = 'not-allowed';
    }
    
    if (submitText) submitText.textContent = 'Processing...';
    if (spinner) spinner.style.display = 'block';
  },

  /**
   * Hide loading state
   */
  hideLoading() {
    const submitBtn = document.getElementById('submit-offer-btn');
    const submitText = document.getElementById('submit-text');
    const spinner = document.getElementById('loading-spinner');
    
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.style.background = '#007cba';
      submitBtn.style.cursor = 'pointer';
    }
    
    if (submitText) submitText.textContent = 'Submit Offer';
    if (spinner) spinner.style.display = 'none';
  }
};

// Global functions for onclick handlers
function handleCurrencySelection() {
  MultiStepOfferModal.handleCurrencySelection();
}

function showStep3() {
  MultiStepOfferModal.showStep(3);
}

function updateOfferDisplay() {
  MultiStepOfferModal.updateOfferDisplay();
}

function submitOffer() {
  return MultiStepOfferModal.submitOffer();
}

function closeOfferModal() {
  MultiStepOfferModal.closeModal();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => MultiStepOfferModal.init());
} else {
  MultiStepOfferModal.init();
}

// Make available globally
if (typeof window !== 'undefined') {
  window.MultiStepOfferModal = MultiStepOfferModal;
}